// src/components/Layout.js
import React, { useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import AppHeader from './AppHeader';
import { useEntityContext } from './EntityContextSwitch';
import MobileBottomNav from './Navigation/MobileBottomNav';
import DesktopSidebar from './Navigation/DesktopSidebar';
import FileUploadButton from './Notebook/FileUploadButton';
import { UploadProvider } from '../contexts/UploadContext';
import { RecorderProvider } from '../contexts/RecorderContext';
import FloatingRecorder from './AudioRecorder/FloatingRecorder';
import RecorderMetadataModal from './AudioRecorder/RecorderMetadataModal';

const Layout = () => {
  const { currentEntity } = useEntityContext();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileUploadRef = useRef(null);

  const isFullPageRoute = location.pathname === '/chat';

  return (
    <RecorderProvider>
    <UploadProvider fileUploadRef={fileUploadRef}>
      {/* Skip link for keyboard accessibility (WCAG 2.4.1) */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          zIndex: 9999,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          padding: '8px 16px',
          textDecoration: 'none',
          borderRadius: '0 0 4px 0',
          fontWeight: 'bold',
          '&:focus': {
            position: 'fixed',
            left: 0,
            top: 0,
          },
        }}
      >
        Skip to main content
      </Box>

      <Box sx={{ display: 'flex', minHeight: '100vh', maxWidth: '100vw', overflow: 'hidden' }}>
        {!isMobile && <DesktopSidebar />}

        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AppHeader />
          <Box
            id="main-content"
            tabIndex={-1}
            sx={{
              flexGrow: 1,
              // Remove padding and margin for full-page routes
              ...(isFullPageRoute ? {
                p: 0,
                mt: '64px',
                width: '100%',
                height: 'calc(100vh - 64px)',
                overflow: 'hidden'
              } : {
                p: { xs: 2, sm: 3 },
                pb: isMobile ? 9 : 3,
                mt: '64px',
                maxWidth: 1600,
                mx: 'auto',
                width: '100%',
                overflowX: 'hidden',
                boxSizing: 'border-box'
              })
            }}
          >
            <Outlet />
          </Box>
        </Box>

        {isMobile && <MobileBottomNav />}

        {/* Hidden global file upload - can be triggered from anywhere */}
        <Box sx={{ display: 'none' }} ref={fileUploadRef}>
          <FileUploadButton
            entityId={currentEntity?.id}
            onUploadComplete={(files) => {
              console.log('Global upload complete:', files);
            }}
          />
        </Box>

        {/* Floating Recorder - persists across navigation */}
        <FloatingRecorder />
        <RecorderMetadataModal />

      </Box>
    </UploadProvider>
    </RecorderProvider>
  );
};

export default Layout;